import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Heart, MessageCircle, Send, Volume2, VolumeX, Play, Pause, Sparkles, ExternalLink, ChevronUp, ChevronDown,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getAdsPool } from "@/lib/adsPool";
import { useMusicAutoplay, MusicNowPlaying } from "@/components/MusicLocation";
import { useNavigate } from "react-router-dom";
import { Bookmark, Share2 } from "lucide-react";
import { useSavedPosts } from "@/hooks/useSavedPosts";
import FollowButton from "@/components/FollowButton";
import { linkify } from "@/components/ExpandableText";
import CommentThread from "@/components/CommentThread";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AD_EVERY = 3; // insert 1 sponsored ad after every 3 videos

/**
 * VideoReels — full-screen vertical snap-scroll video feed (TikTok / FB Reels style).
 * - Opens over the current page when a user taps a video in Feed.
 * - Autoplays the currently visible video; pauses others.
 * - Interleaves a sponsored ad every 3 videos with a prominent "Patrocinado" tag.
 *
 * Props:
 *  - videos: [{ post_id, video_url, author_name, author_picture, content, likes, comments }]
 *  - startId: post_id to start on
 *  - onClose: fn
 *  - onLike(post_id) — parent updates local state
 *  - onComment(post_id, text) — parent posts + updates
 *  - initialMuted: bool
 */
export default function VideoReels({ videos, startId, onClose, onLike, onComment, onCommentsChange, initialMuted = true, onReachEnd }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isSaved, toggle: toggleSave } = useSavedPosts();
  const [shareCounts, setShareCounts] = useState({});
  const shareReel = async (v) => {
    const url = `${window.location.origin}/post/${v.post_id}`;
    try {
      if (navigator.share) await navigator.share({ title: "FUTURO PDV", text: (v.content || "").slice(0, 100), url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); }
      axios.post(`${API}/posts/${v.post_id}/share`).then((r) => setShareCounts((s) => ({ ...s, [v.post_id]: r.data.share_count }))).catch(() => {});
    } catch { /* cancelled */ }
  };
  const saveReel = async (v) => {
    try { const now = await toggleSave(v.post_id); toast.success(now ? "Reel salvo" : "Removido dos salvos"); }
    catch { toast.error("Erro ao salvar"); }
  };
  const [muted, setMuted] = useState(initialMuted);
  const [ads, setAds] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pausedByUser, setPausedByUser] = useState({}); // { slideIndex: bool }
  const [commentDrawer, setCommentDrawer] = useState(null); // post_id
  const [commentOverrides, setCommentOverrides] = useState({}); // post_id -> comments (thread edits while open)
  const [videoErrors, setVideoErrors] = useState({});
  const containerRef = useRef(null);
  const videoRefs = useRef({}); // slideIndex -> HTMLVideoElement
  const impressedAdsRef = useRef(new Set());

  // Build interleaved slide list: video, video, video, AD, video, video, video, AD ...
  const slides = useMemo(() => {
    const out = [];
    videos.forEach((v, i) => {
      out.push({ type: "video", data: v });
      // insert ad after each block of AD_EVERY videos, but not at the very end
      if ((i + 1) % AD_EVERY === 0 && i < videos.length - 1) {
        out.push({ type: "ad_slot", index: Math.floor((i + 1) / AD_EVERY) - 1 });
      }
    });
    return out;
  }, [videos]);

  // Fetch ad pool via shared cache (won't get wiped by media-heavy page transients).
  useEffect(() => {
    let mounted = true;
    getAdsPool().then((data) => {
      if (!mounted) return;
      const all = (data || []).filter((a) => a.image_url || a.video_url || a.description || a.title);
      if (!all.length) return;
      const sp  = all.filter((a) => a.sponsored);
      const rst = all.filter((a) => !a.sponsored);
      setAds([...sp, ...rst]);
    });
    return () => { mounted = false; };
  }, []);

  // Attach ad object to each ad_slot slide (cycles through ads by slot index).
  // Skip slots when no sponsored ads exist so scroll-nav indices stay contiguous.
  const enrichedSlides = useMemo(() => {
    const out = [];
    slides.forEach((s) => {
      if (s.type !== "ad_slot") { out.push(s); return; }
      if (!ads.length) return; // skip empty ad slot
      out.push({ type: "ad", data: ads[s.index % ads.length], slotIndex: s.index });
    });
    return out;
  }, [slides, ads]);

  // Music of the active slide plays (looped) while visible; video is muted meanwhile.
  const activeSlide = enrichedSlides[activeIndex];
  const activeMusic = activeSlide?.type === "video" ? activeSlide.data?.music : null;
  const musicPlaying = useMusicAutoplay(activeMusic, !!activeMusic?.preview_url && !pausedByUser[activeIndex]);

  // Start at startId
  useEffect(() => {
    if (!startId || !containerRef.current) return;
    const idx = enrichedSlides.findIndex((s) => s.type === "video" && s.data.post_id === startId);
    if (idx < 0) return;
    const container = containerRef.current;
    const target = container.children[idx];
    if (target) {
      target.scrollIntoView({ behavior: "instant", block: "start" });
      setActiveIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startId, enrichedSlides.length]);

  // Observe which slide is currently in view.
  // Uses many thresholds and picks the slide with the highest intersection ratio,
  // guaranteeing exactly one active slide even during fast scrolls.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ratios = new Map();
    const pickActive = () => {
      let bestEl = null;
      let bestRatio = 0;
      ratios.forEach((r, el) => {
        if (r > bestRatio) { bestRatio = r; bestEl = el; }
      });
      if (bestEl && bestRatio >= 0.5) {
        const idx = Number(bestEl.dataset.slideIdx);
        setActiveIndex((cur) => (cur === idx ? cur : idx));
      }
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => ratios.set(e.target, e.intersectionRatio));
        pickActive();
      },
      { root: container, threshold: [0, 0.2, 0.4, 0.6, 0.8, 1.0] },
    );
    Array.from(container.children).forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [enrichedSlides.length]);

  // Autoplay active video, pause every other one; track ad impressions.
  // Order matters: pause all non-active FIRST, then play the active one,
  // so we never have two videos playing simultaneously.
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([k, vid]) => {
      if (!vid || Number(k) === activeIndex) return;
      if (!vid.paused) { try { vid.pause(); } catch { /* noop */ } }
      // Only rewind when the video actually has data — a seek on readyState=0
      // triggers NotSupportedError against endpoints that don't serve Range.
      if (vid.readyState >= 1 && vid.currentTime > 0) {
        try { vid.currentTime = 0; } catch { /* noop */ }
      }
    });
    const active = videoRefs.current[activeIndex];
    if (active && active.paused && !pausedByUser[activeIndex]) {
      const playPromise = active.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => { /* autoplay blocked or aborted */ });
      }
    }
    const cur = enrichedSlides[activeIndex];
    if (cur?.type === "ad" && cur.data?.ad_id && !impressedAdsRef.current.has(cur.data.ad_id)) {
      impressedAdsRef.current.add(cur.data.ad_id);
      axios.post(`${API}/ads/${cur.data.ad_id}/impression`).catch(() => {});
    }
  }, [activeIndex, enrichedSlides, pausedByUser]);

  // Extra safety: whenever the tab visibility changes (user tabs away), pause everything.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") {
        Object.values(videoRefs.current).forEach((vid) => {
          if (vid && !vid.paused) { try { vid.pause(); } catch { /* noop */ } }
        });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Lock body scroll while the overlay is mounted so the underlying page can't peek through.
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  const retryVideo = (idx) => {
    setVideoErrors((e) => {
      const next = { ...e };
      delete next[idx];
      return next;
    });
    const vid = videoRefs.current[idx];
    if (vid) {
      try { vid.load(); } catch { /* noop */ }
    }
  };

  // Global keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") scrollBy(1);
      if (e.key === "ArrowUp") scrollBy(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const scrollBy = (delta) => {
    const container = containerRef.current;
    if (!container) return;
    const next = Math.min(Math.max(0, activeIndex + delta), enrichedSlides.length - 1);
    const target = container.children[next];
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const togglePlay = (idx) => {
    const vid = videoRefs.current[idx];
    if (!vid) return;
    if (vid.paused) {
      vid.play().then(() => setPausedByUser({ ...pausedByUser, [idx]: false })).catch(() => {});
    } else {
      vid.pause();
      setPausedByUser({ ...pausedByUser, [idx]: true });
    }
  };

  const trackAdClick = (adId) => axios.post(`${API}/ads/${adId}/click`).catch(() => {});

  if (!videos?.length) return null;

  return (
    <div
      className="fixed inset-0 z-[150] bg-black"
      data-testid="video-reels"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        data-testid="reels-close"
        className="absolute top-4 right-4 z-30 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center"
        aria-label="Fechar"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Mute toggle */}
      <button
        onClick={() => setMuted((m) => !m)}
        data-testid="reels-mute"
        className="absolute top-4 left-4 z-30 h-11 px-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center gap-2"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        <span className="text-xs">{muted ? "Sem som" : "Com som"}</span>
      </button>

      {/* Up / down nav buttons (desktop) */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2 hidden md:flex">
        <button onClick={() => scrollBy(-1)} data-testid="reels-prev" className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center" aria-label="Anterior">
          <ChevronUp className="w-5 h-5" />
        </button>
        <button onClick={() => scrollBy(1)} data-testid="reels-next" className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center" aria-label="Próximo">
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>

      {/* Vertical snap container */}
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none" }}
      >
        {enrichedSlides.map((s, idx) => {
          if (s.type === "ad") {
            const a = s.data;
            const slotKey = `ad-${idx}-${s.slotIndex ?? 0}-${a.ad_id}`;
            return (
              <div
                key={slotKey}
                data-slide-idx={idx}
                data-testid={`reels-ad-slide-${s.slotIndex ?? 0}-${a.ad_id}`}
                className="h-screen w-full snap-start relative flex items-center justify-center overflow-hidden"
              >
                {a.image_url && (
                  <img src={a.image_url} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <div className="relative z-10 max-w-md w-full px-6 flex flex-col items-center text-center gap-4">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest" data-testid={`reels-sponsored-tag-${s.slotIndex ?? 0}-${a.ad_id}`}>
                    <Sparkles className="w-3.5 h-3.5" /> Patrocinado
                  </div>
                  {a.video_url ? (
                    <video src={a.video_url} className="w-full max-h-[55vh] rounded-2xl object-cover shadow-2xl bg-black" muted loop playsInline autoPlay controls data-testid={`reels-ad-video-${a.ad_id}`} />
                  ) : a.image_url && (
                    <img
                      src={a.image_url}
                      alt={a.title}
                      className="w-full max-h-[55vh] rounded-2xl object-cover shadow-2xl"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <h3 className="text-white text-2xl font-bold tracking-tight">{a.title}</h3>
                  <p className="text-white/80 text-sm">{a.description}</p>
                  <div className="text-[11px] text-white/50">por {a.author_name}</div>
                  {a.target_url && (
                    <a
                      href={a.target_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAdClick(a.ad_id)}
                      data-testid={`reels-sponsored-cta-${s.slotIndex ?? 0}-${a.ad_id}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-colors"
                    >
                      {a.cta || "Saiba mais"} <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            );
          }

          const v = s.data;
          const liked = v.likes?.includes?.(user?.user_id);
          const isPaused = pausedByUser[idx];
          return (
            <div
              key={v.post_id}
              data-slide-idx={idx}
              data-testid={`reels-slide-${v.post_id}`}
              className="h-screen w-full snap-start relative flex items-center justify-center bg-black"
            >
              <video
                ref={(el) => { videoRefs.current[idx] = el; }}
                src={v.video_url}
                poster={v.cover_url || undefined}
                className="max-h-full max-w-full object-contain cursor-pointer"
                playsInline
                loop
                muted={muted || !!v.music?.preview_url}
                onClick={() => togglePlay(idx)}
                onError={() => setVideoErrors((e) => ({ ...e, [idx]: true }))}
                data-testid={`reels-video-${v.post_id}`}
              />

              {videoErrors[idx] && (
                <div className="absolute inset-0 flex items-center justify-center p-6 text-center" data-testid={`reels-video-error-${v.post_id}`}>
                  <div className="max-w-xs pointer-events-auto">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/10 flex items-center justify-center">
                      <VolumeX className="w-7 h-7 text-white/60" />
                    </div>
                    <div className="text-white text-lg font-semibold">Vídeo indisponível</div>
                    <div className="text-white/60 text-xs mt-1 mb-4">
                      Este vídeo não pôde ser carregado.
                    </div>
                    <button
                      onClick={() => retryVideo(idx)}
                      data-testid={`reels-video-retry-${v.post_id}`}
                      className="h-9 px-4 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold backdrop-blur-md transition-colors"
                    >
                      Tentar novamente
                    </button>
                  </div>
                </div>
              )}

              {isPaused && !videoErrors[idx] && (
                <button
                  onClick={() => togglePlay(idx)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30"
                  aria-label="Reproduzir"
                >
                  <span className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </span>
                </button>
              )}

              {/* Bottom gradient + info */}
              <div className="absolute bottom-0 left-0 right-0 p-4 pb-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none">
                <div className="max-w-md mx-auto space-y-2 pointer-events-auto">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-10 h-10 ring-2 ring-white/40">
                      <AvatarImage src={v.author_picture} />
                      <AvatarFallback>{v.author_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <Link to={`/user/${v.user_id}`} onClick={onClose} className="font-semibold text-white text-sm truncate block hover:underline" data-testid={`reels-author-${v.post_id}`}>
                        {v.author_name}
                      </Link>
                      <div className="text-[10px] text-white/60">
                        {new Date(v.created_at).toLocaleDateString("pt-BR")}{v.location?.name ? ` · 📍 ${v.location.name}` : ""}
                      </div>
                    </div>
                    {v.user_id !== user?.user_id && <FollowButton userId={v.user_id} size="sm" variant="secondary" />}
                  </div>
                  {v.content && (
                    <p className="text-white/90 text-sm line-clamp-3 whitespace-pre-wrap" data-testid={`reels-caption-${v.post_id}`}>
                      {linkify(v.content, { dark: true })}
                    </p>
                  )}
                  {v.music?.preview_url && (
                    <button type="button" onClick={() => { onClose?.(); navigate(`/music/${v.music_key || (v.music.deezer_id ? `dz_${v.music.deezer_id}` : "")}`, { state: { music: v.music } }); }}
                      className="text-left hover:opacity-90 transition-opacity" data-testid={`reels-music-link-${v.post_id}`} title="Ver outros vídeos com esta música">
                      <MusicNowPlaying music={v.music} playing={idx === activeIndex && musicPlaying} testid={`reels-music-${v.post_id}`} />
                    </button>
                  )}
                </div>
              </div>

              {/* Right action rail */}
              <div className="absolute right-3 bottom-24 md:right-16 flex flex-col gap-4 z-20">
                <button
                  onClick={() => onLike?.(v.post_id)}
                  data-testid={`reels-like-${v.post_id}`}
                  className="flex flex-col items-center gap-1 group"
                  aria-label="Curtir"
                >
                  <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Heart className={`w-6 h-6 ${liked ? "fill-red-500 text-red-500" : "text-white"}`} />
                  </span>
                  <span className="text-white text-[11px] font-semibold">{v.likes?.length || 0}</span>
                </button>
                <button
                  onClick={() => setCommentDrawer(v.post_id)}
                  data-testid={`reels-comment-btn-${v.post_id}`}
                  className="flex flex-col items-center gap-1 group"
                  aria-label="Comentários"
                >
                  <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </span>
                  <span className="text-white text-[11px] font-semibold">{(commentOverrides[v.post_id] || v.comments)?.length || 0}</span>
                </button>
                <button onClick={() => shareReel(v)} data-testid={`reels-share-${v.post_id}`} className="flex flex-col items-center gap-1 group" aria-label="Compartilhar">
                  <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Share2 className="w-6 h-6 text-white" />
                  </span>
                  <span className="text-white text-[11px] font-semibold">{shareCounts[v.post_id] ?? (v.share_count || 0)}</span>
                </button>
                <button onClick={() => saveReel(v)} data-testid={`reels-save-${v.post_id}`} data-saved={isSaved(v.post_id) ? "1" : "0"} className="flex flex-col items-center gap-1 group" aria-label="Salvar">
                  <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Bookmark className={`w-6 h-6 ${isSaved(v.post_id) ? "fill-white text-white" : "text-white"}`} />
                  </span>
                  <span className="text-white text-[11px] font-semibold">{isSaved(v.post_id) ? "Salvo" : "Salvar"}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Comment drawer */}
      {commentDrawer && (
        <div
          className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center"
          onClick={() => setCommentDrawer(null)}
          data-testid="reels-comment-drawer"
        >
          <div
            className="w-full md:max-w-md bg-card rounded-t-3xl md:rounded-3xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold">Comentários</h3>
              <button onClick={() => setCommentDrawer(null)} className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center" data-testid="reels-comment-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(() => {
                const post = videos.find((x) => x.post_id === commentDrawer);
                const comments = commentOverrides[commentDrawer] || post?.comments || [];
                const setC = (fn) => {
                  const next = fn(comments);
                  setCommentOverrides((o) => ({ ...o, [commentDrawer]: next }));
                  onCommentsChange?.(commentDrawer, next);
                };
                return (
                  <CommentThread
                    postId={commentDrawer}
                    comments={comments}
                    inputTestId="reels"
                    onNewComment={(c) => { setC((cs) => [...cs, c]); toast.success("Comentário enviado"); }}
                    onLocalUpdate={(u) => setC((cs) => cs.map((c) => c.comment_id === u.comment_id ? u : c))}
                    onEdited={(u) => setC((cs) => cs.map((c) => c.comment_id === u.comment_id ? u : c))}
                    onDeleted={(id) => setC((cs) => cs.filter((c) => c.comment_id !== id && c.parent_comment_id !== id))}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
